<script>
  import Panel from './Panel.svelte';
  import Alert from './Alert.svelte';
  import Button from './Button.svelte';
  import Code from './Code.svelte';
  import DataTable from './DataTable.svelte';
  import DistributionTable from './DistributionTable.svelte';
  import PeriodChips from './PeriodChips.svelte';
  import { go } from '../lib/nav.svelte.js';
  import { queryDraft } from '../lib/queryDraft.svelte.js';
  import { compareQueryValues } from '../lib/valueSort.js';

  // One stored query → one panel. The renderer keys on which result-shape
  // convention variables are present (series / publisher / period / value /
  // count); a result outside the convention falls back to a plain table, never
  // an error.
  //
  // Blocks are keyed on ?series when the query selects one, else on ?publisher.
  // A compiled comparison can put two cubes from the SAME institution side by
  // side, which is exactly what publisher-keyed blocks cannot express — they
  // would draw one heading over both cubes' numbers. Every hand-written query
  // predates ?series and is unaffected.
  //
  // The known block set is who could have answered, supplied rather than derived
  // from the rows: `query.series` for a comparison (the labels its author gave
  // the sides), `publishers` for a publisher-keyed panel (the own institution
  // plus every readable partner, from the analysis job). A side that answered
  // nothing has no row to derive a heading from, and an omitted block reads as
  // "not part of this comparison" when it means "answers nothing here".
  //
  // Blocks sit side by side, and each keeps its own vocabulary, its own
  // denominator and its own percentages: nothing is pivoted onto a shared row
  // axis, so nothing here depends on aligning "Female" with "Femenino".
  let { query, result, publishers = [] } = $props();

  let selectedPeriod = $state('all');
  let showCode = $state(false);

  const num = (v) => Number(v) || 0;
  const cols = $derived(result.columns ?? []);
  const hasSeries = $derived(cols.includes('series'));
  const hasPublisher = $derived(cols.includes('publisher'));
  const keyVar = $derived(hasSeries ? 'series' : hasPublisher ? 'publisher' : null);
  const hasPeriod = $derived(cols.includes('period'));
  const convention = $derived(cols.includes('value') && cols.includes('count'));

  const periods = $derived(hasPeriod ? [...new Set(result.rows.map((r) => r.period))].sort() : []);

  // Filter to the selected period (or keep all), then aggregate to
  // (publisher?, value) — "All periods" sums a value across every quarter.
  const groups = $derived.by(() => {
    if (!convention) return [];
    let rows = result.rows;
    if (hasPeriod && selectedPeriod !== 'all') rows = rows.filter((r) => r.period === selectedPeriod);
    const byBlock = new Map();
    for (const r of rows) {
      const key = keyVar ? r[keyVar] : null;
      if (!byBlock.has(key)) byBlock.set(key, new Map());
      const m = byBlock.get(key);
      m.set(r.value, (m.get(r.value) ?? 0) + num(r.count));
    }
    // Known blocks first, in the order they were declared (the author's side
    // order for a comparison; own institution then partners otherwise); any key
    // in the rows that is not in that list still gets a block — dropping data to
    // keep a tidy column set would be the worse failure of the two.
    const known = hasSeries
      ? (query.series ?? [])
      : hasPublisher ? publishers.map((p) => p.label) : [null];
    const order = [...known, ...[...byBlock.keys()].filter((k) => !known.includes(k))];

    return order.map((label) => {
      const m = byBlock.get(label) ?? new Map();
      // `values` is a one-element array so the rows go straight into the shared
      // DistributionTable, which takes one cell per column and here has one.
      const items = [...m.entries()].map(([value, count]) => ({ values: [value], count }))
        .sort((a, b) => compareQueryValues(a, b, query.valueSort));
      return { label, total: items.reduce((s, i) => s + i.count, 0), items };
    });
  });

  function openInCustom() {
    queryDraft.sparql = query.sparql;
    go('queries');
  }
</script>

<Panel title={query.label}>
  {#if query.description}<p class="wf2-caption">{query.description}</p>{/if}

  {#if result.error}
    <Alert title="This query failed" variant="danger">
      <p class="mono">{result.error}</p>
    </Alert>
  {:else if !convention}
    <!-- Outside the ?publisher ?value ?period ?count convention: plain table. -->
    {#if result.rows.length}
      <DataTable columns={cols} rows={result.rows.map((r) => cols.map((c) => r[c]))} />
    {:else}
      <p class="muted">No rows.</p>
    {/if}
  {:else}
    {#if hasPeriod}
      <PeriodChips {periods} selected={selectedPeriod} onselect={(p) => (selectedPeriod = p)} />
    {/if}

    <div class="blocks" style="--cols:{Math.max(1, groups.length)}">
      {#each groups as group}
        <div class="block">
          {#if keyVar}
            <h4 class="wf2-title wf2-title-level-4">
              {group.label}{#if group.items.length} · {group.total} {query.unit ?? 'records'}{/if}
            </h4>
          {/if}
          {#if group.items.length}
            <DistributionTable columns={['Value']} rows={group.items} total={group.total} />
          {:else}
            <!-- Present and labelled, not omitted. The block is the statement:
                 this side of the comparison answers nothing here. -->
            <p class="empty muted">
              {hasSeries
                ? `${group.label} returns nothing`
                : `${group.label} publishes no cube answering this query`}{#if hasPeriod && selectedPeriod !== 'all'} for {selectedPeriod}{/if}.
            </p>
          {/if}
        </div>
      {/each}
    </div>
    {#if !groups.some((g) => g.items.length)}
      <p class="muted">No publisher returned rows for this selection.</p>
    {/if}
  {/if}

  <div class="wf2-row" style="gap:12px; margin-top: var(--space-2)">
    <Button variant="ghost" onclick={() => (showCode = !showCode)}>{showCode ? 'Hide SPARQL' : 'Show SPARQL'}</Button>
    {#if showCode}
      <Button variant="secondary" onclick={openInCustom}>Open in custom query</Button>
    {/if}
  </div>
  {#if showCode}
    <Code lang="sparql" text={query.sparql} />
  {/if}
</Panel>

<style>
  /* One column per publisher, side by side — the comparison the whole federated
     read exists to make. minmax(0,1fr) rather than 1fr so a wide value label
     scrolls inside its own table instead of pushing the grid past the page. */
  .blocks {
    display: grid;
    grid-template-columns: repeat(var(--cols), minmax(0, 1fr));
    gap: var(--space-4);
    margin-bottom: var(--space-3);
  }
  /* Two tables do not fit narrow; below the app's breakpoint they stack. */
  @media (max-width: 720px) {
    .blocks { grid-template-columns: 1fr; }
  }
  .empty {
    border: 1px dashed var(--border);
    border-radius: var(--radius);
    padding: var(--space-3);
    font-size: .9rem;
  }
</style>
